import {t} from '@lingui/macro';
import _ from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {ClickEventHandler, intersperseNodes} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {ChildrenCell} from '../../../../components/common/table/cells/childrenCell';
import {DateCell} from '../../../../components/common/table/cells/dateCell';
import {EmptyCell} from '../../../../components/common/table/cells/emptyCell';
import {NameCell} from '../../../../components/common/table/cells/nameCell';
import {TeammateCell} from '../../../../components/common/table/cells/teammateCell';
import {TableColumn} from '../../../../components/common/table/tableColumn';
import {TableRow, TableRowRenderOptions} from '../../../../components/common/table/tableRow';
import {ApiTokenScopeModel} from '../../../../helpers/apiTokenHelpers';
import {ApiTokenModel} from '../../../../models/api/apiTokenModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {DevelopersApiTokensTableColumnsEnum} from './developersApiTokensTableColumns';

/*
 * Props.
 */

interface DevelopersApiTokensTableRowProps {
  tableColumns: ReadonlyArray<TableColumn<DevelopersApiTokensTableColumnsEnum>>;
  token?: ApiTokenModel;
  options: TableRowRenderOptions;
  owner?: TeammateModel;
  scopes: ReadonlyArray<ApiTokenScopeModel>;

  onSelectApiToken: ClickEventHandler;
}

/*
 * Style.
 */

interface StyledTableRowProps {
  $minHeight: number;
}
const StyledTableRow = styled(TableRow)<StyledTableRowProps>`
  min-height: ${(p) => p.$minHeight}px;
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  margin-left: 12px;
`;

/*
 * Component.
 */

export const DevelopersApiTokensTableRow: FC<DevelopersApiTokensTableRowProps> = (props) => {
  const {token, options, tableColumns, owner, scopes, onSelectApiToken} = props;
  const localize = useLocalize();

  if (!token) {
    return null;
  }

  const cellRenderer = (
    index: number,
    column: TableColumn<DevelopersApiTokensTableColumnsEnum>,
    isHovering: boolean,
    shouldShowStickyGradient: boolean,
  ) => {
    switch (column.type) {
      case DevelopersApiTokensTableColumnsEnum.NAME:
        return (
          <NameCell
            key={DevelopersApiTokensTableColumnsEnum.NAME}
            value={token.name ?? localize(t('settings-developers-api-tokens-untitle-name')`Untitled`)}
            description={localize(
              t('settings-developers-api-tokens-name-token-id')`Token ID: ${token.publicId}`,
            )}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      case DevelopersApiTokensTableColumnsEnum.SCOPE:
        return (
          <ChildrenCell
            key={DevelopersApiTokensTableColumnsEnum.SCOPE}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
          >
            <StyledTooltipCoordinator render={() => renderScopes(scopes, token.scopes)}>
              <TooltipOverflowCondition>{renderScopes(scopes, token.scopes)}</TooltipOverflowCondition>
            </StyledTooltipCoordinator>
          </ChildrenCell>
        );
      case DevelopersApiTokensTableColumnsEnum.LAST_ACTIVITY:
        return (
          <DateCell
            key={DevelopersApiTokensTableColumnsEnum.LAST_ACTIVITY}
            value={token.lastUsedAt}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            customFormat={{
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            }}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      case DevelopersApiTokensTableColumnsEnum.CREATOR: {
        if (!owner) {
          return (
            <EmptyCell settings={column.settings} isHovering={isHovering} size={options.size} index={index} />
          );
        }
        return (
          <TeammateCell
            key={DevelopersApiTokensTableColumnsEnum.CREATOR}
            value={owner}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      }
      default: {
        return (
          <EmptyCell settings={column.settings} isHovering={isHovering} size={options.size} index={index} />
        );
      }
    }
  };

  return (
    <StyledTableRow
      onSelect={(event) => {
        if (event.defaultPrevented) {
          return;
        }
        onSelectApiToken(event);
      }}
      renderColumn={(isHovering) =>
        tableColumns.map((column, index) =>
          cellRenderer(index, column, isHovering, Boolean(options.shouldShowStickyGradient)),
        )
      }
      hasHover
      $minHeight={options.tableRowHeight}
      shouldShowRightGradient={options.shouldShowRightGradient}
    />
  );
};

/*
 * Helper.
 */

function renderScopes(scopes: ReadonlyArray<ApiTokenScopeModel>, selectedScopes: ReadonlyArray<string>) {
  return intersperseNodes(
    _([...selectedScopes])
      .map((selectedScope) => {
        const foundScope = scopes.find((scope) => scope.value === selectedScope);
        if (foundScope) {
          return {
            node: foundScope.display,
            key: foundScope.value,
          };
        }
        return {
          node: selectedScope,
          key: selectedScope,
        };
      })
      .value(),
    () => ', ',
  );
}
